/**
 * Agent Tally Service
 * Handles agent requests for Tally data and formats responses for chat interface
 */

import { TallyConnectorService } from './tallyConnector';
import { useConnectionStore } from '../stores/connectionStore';
import { api } from './api';

export interface AgentTallyQuery {
  query: string;
  sessionId: string;
  queryType?: 'report' | 'data' | 'search' | 'analysis';
  parameters?: Record<string, any>;
}

export interface AgentTallyResponse {
  success: boolean;
  data?: any;
  formattedResponse?: string;
  error?: string;
  metadata?: {
    company: string;
    queryType: string;
    timestamp: string;
    processingTime: number;
  };
}

export class AgentTallyService {
  /**
   * Process agent query for Tally data
   */
  static async processTallyQuery(query: AgentTallyQuery): Promise<AgentTallyResponse> {
    const startTime = Date.now();
    const connectionStore = useConnectionStore.getState();
    
    // Validate connection state
    const validationResult = this.validateConnection(connectionStore);
    if (!validationResult.valid) {
      return {
        success: false,
        error: validationResult.error,
        metadata: {
          company: connectionStore.selectedCompany || 'none',
          queryType: query.queryType || 'unknown',
          timestamp: new Date().toISOString(),
          processingTime: Date.now() - startTime
        }
      };
    }

    try {
      // Determine query type if not specified
      const queryType = query.queryType || this.detectQueryType(query.query);
      
      let tallyData: any;
      
      switch (queryType) {
        case 'report':
          tallyData = await this.getTallyReport(query);
          break;
        case 'data':
          tallyData = await this.getTallyData(query);
          break;
        case 'search':
          tallyData = await this.searchTallyData(query);
          break;
        case 'analysis':
          tallyData = await this.analyzeTallyData(query);
          break;
        default:
          tallyData = await this.getGenericTallyData(query);
      }

      const formattedResponse = this.formatTallyDataForChat(tallyData, queryType);
      
      return {
        success: true,
        data: tallyData,
        formattedResponse,
        metadata: {
          company: connectionStore.selectedCompany || 'unknown',
          queryType,
          timestamp: new Date().toISOString(),
          processingTime: Date.now() - startTime
        }
      };
    } catch (error) {
      console.error('Agent Tally query failed:', error);
      
      return {
        success: false,
        error: this.formatError(error),
        metadata: {
          company: connectionStore.selectedCompany || 'unknown',
          queryType: query.queryType || 'unknown',
          timestamp: new Date().toISOString(),
          processingTime: Date.now() - startTime
        }
      };
    }
  }

  /**
   * Validate connection state for Tally queries
   */
  private static validateConnection(connectionStore: any): {
    valid: boolean;
    error?: string;
  } {
    if (connectionStore.status !== 'connected') {
      return {
        valid: false,
        error: `Tally is not connected (status: ${connectionStore.status}). Please ensure TallyConnector is installed and Tally is running.`
      };
    }

    if (!connectionStore.selectedCompany) {
      return {
        valid: false,
        error: 'No Tally company selected. Please select a company from the connection panel first.'
      };
    }

    if (!connectionStore.connectionId) {
      return {
        valid: false,
        error: 'Connection ID not available. Please refresh the connection and try again.'
      };
    }

    return { valid: true };
  }

  /**
   * Detect query type from query text
   */
  private static detectQueryType(query: string): 'report' | 'data' | 'search' | 'analysis' {
    const lowerQuery = query.toLowerCase();
    
    if (lowerQuery.includes('report') || lowerQuery.includes('statement')) {
      return 'report';
    } else if (lowerQuery.includes('analyze') || lowerQuery.includes('analysis') || lowerQuery.includes('trend')) {
      return 'analysis';
    } else if (lowerQuery.includes('find') || lowerQuery.includes('search') || lowerQuery.includes('lookup')) {
      return 'search';
    } else {
      return 'data';
    }
  }

  /**
   * Get Tally report data
   */
  private static async getTallyReport(query: AgentTallyQuery): Promise<any> {
    const response = await api.post('/api/tally/report', {
      query: query.query,
      session_id: query.sessionId,
      company: useConnectionStore.getState().selectedCompany,
      parameters: query.parameters
    });

    return response.data.data || response.data;
  }

  /**
   * Get general Tally data
   */
  private static async getTallyData(query: AgentTallyQuery): Promise<any> {
    const response = await api.post('/api/tally/data', {
      query: query.query,
      session_id: query.sessionId,
      company: useConnectionStore.getState().selectedCompany,
      parameters: query.parameters
    });

    return response.data.data || response.data;
  }

  /**
   * Search Tally data
   */
  private static async searchTallyData(query: AgentTallyQuery): Promise<any> {
    const response = await api.post('/api/tally/search', {
      query: query.query,
      session_id: query.sessionId,
      company: useConnectionStore.getState().selectedCompany,
      parameters: query.parameters
    });

    return response.data.data || response.data;
  }

  /**
   * Analyze Tally data
   */
  private static async analyzeTallyData(query: AgentTallyQuery): Promise<any> {
    const response = await api.post('/api/tally/analyze', {
      query: query.query,
      session_id: query.sessionId,
      company: useConnectionStore.getState().selectedCompany,
      parameters: query.parameters
    });

    return response.data.data || response.data;
  }

  /**
   * Get generic Tally data
   */
  private static async getGenericTallyData(query: AgentTallyQuery): Promise<any> {
    const response = await api.post('/api/tally/query', {
      query: query.query,
      session_id: query.sessionId,
      company: useConnectionStore.getState().selectedCompany,
      parameters: query.parameters
    });

    return response.data.data || response.data;
  }

  /**
   * Format Tally data for chat display
   */
  static formatTallyDataForChat(data: any, queryType: string): string {
    if (!data) {
      return 'No data received from Tally.';
    }

    try {
      switch (queryType) {
        case 'report':
          return this.formatReportData(data);
        case 'data':
          return this.formatDataResponse(data);
        case 'search':
          return this.formatSearchResults(data);
        case 'analysis':
          return this.formatAnalysisResults(data);
        default:
          return this.formatGenericData(data);
      }
    } catch (error) {
      console.error('Error formatting Tally data:', error);
      return `Data received from Tally, but formatting failed. Raw data available in console.`;
    }
  }

  /**
   * Format report data for display
   */
  private static formatReportData(data: any): string {
    if (data.report_type && data.data) {
      let formatted = `📊 **${data.report_type}**\n\n`;
      
      if (data.period) {
        formatted += `📅 **Period:** ${data.period}\n\n`;
      }

      if (Array.isArray(data.data)) {
        data.data.forEach((item: any, index: number) => {
          formatted += `${index + 1}. **${item.name || item.account || 'Item'}**: ${item.amount || item.value || 'N/A'}\n`;
        });
      } else {
        formatted += JSON.stringify(data.data, null, 2);
      }

      return formatted;
    }

    return this.formatGenericData(data);
  }

  /**
   * Format data response
   */
  private static formatDataResponse(data: any): string {
    if (data.records && Array.isArray(data.records)) {
      let formatted = `📋 **Data Results** (${data.records.length} records)\n\n`;
      
      data.records.slice(0, 10).forEach((record: any, index: number) => {
        formatted += `${index + 1}. `;
        if (record.name) formatted += `**${record.name}** `;
        if (record.amount) formatted += `₹${record.amount} `;
        if (record.date) formatted += `(${record.date})`;
        formatted += '\n';
      });

      if (data.records.length > 10) {
        formatted += `\n... and ${data.records.length - 10} more records.`;
      }

      return formatted;
    }

    return this.formatGenericData(data);
  }

  /**
   * Format search results
   */
  private static formatSearchResults(data: any): string {
    if (data.results && Array.isArray(data.results)) {
      let formatted = `🔍 **Search Results** (${data.results.length} found)\n\n`;
      
      data.results.forEach((result: any, index: number) => {
        formatted += `${index + 1}. **${result.name || result.title}**\n`;
        if (result.type) formatted += `   Type: ${result.type}\n`;
        if (result.amount) formatted += `   Amount: ₹${result.amount}\n`;
        formatted += '\n';
      });

      return formatted;
    }

    return this.formatGenericData(data);
  }

  /**
   * Format analysis results
   */
  private static formatAnalysisResults(data: any): string {
    if (data.analysis) {
      let formatted = `📈 **Analysis Results**\n\n`;
      
      if (data.summary) {
        formatted += `**Summary:** ${data.summary}\n\n`;
      }

      if (data.trends && Array.isArray(data.trends)) {
        formatted += `**Key Trends:**\n`;
        data.trends.forEach((trend: string) => {
          formatted += `• ${trend}\n`;
        });
        formatted += '\n';
      }

      if (data.recommendations && Array.isArray(data.recommendations)) {
        formatted += `**Recommendations:**\n`;
        data.recommendations.forEach((rec: string) => {
          formatted += `• ${rec}\n`;
        });
      }

      return formatted;
    }

    return this.formatGenericData(data);
  }

  /**
   * Format generic data
   */
  private static formatGenericData(data: any): string {
    if (typeof data === 'string') {
      return data;
    }

    if (typeof data === 'object') {
      return `📄 **Tally Data:**\n\`\`\`json\n${JSON.stringify(data, null, 2)}\n\`\`\``;
    }

    return String(data);
  }

  /**
   * Format error for user display
   */
  private static formatError(error: any): string {
    if (error?.response?.data?.message) {
      return error.response.data.message;
    }

    if (error?.message) {
      return error.message;
    }

    if (typeof error === 'string') {
      return error;
    }

    return 'An unexpected error occurred while accessing Tally data.';
  }

  /**
   * Get available Tally operations for the current company
   */
  static async getAvailableOperations(): Promise<string[]> {
    try {
      const connectionStore = useConnectionStore.getState();
      
      if (connectionStore.status !== 'connected' || !connectionStore.selectedCompany) {
        return [];
      }

      const response = await api.get('/api/tally/operations', {
        params: {
          company: connectionStore.selectedCompany
        }
      });

      return response.data.operations || [];
    } catch (error) {
      console.error('Failed to get available operations:', error);
      return [];
    }
  }

  /**
   * Test Tally connection and data access
   */
  static async testTallyAccess(): Promise<{
    success: boolean;
    message: string;
    capabilities: string[];
  }> {
    try {
      const connectionStore = useConnectionStore.getState();
      
      if (connectionStore.status !== 'connected') {
        return {
          success: false,
          message: 'Tally not connected',
          capabilities: []
        };
      }

      const response = await api.get('/api/tally/test', {
        params: {
          company: connectionStore.selectedCompany
        }
      });

      return {
        success: true,
        message: 'Tally access working correctly',
        capabilities: response.data.capabilities || []
      };
    } catch (error) {
      return {
        success: false,
        message: this.formatError(error),
        capabilities: []
      };
    }
  }
}

export default AgentTallyService;